import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { companiesAPI, jobsAPI, solicitationsAPI, applicationsAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import OnboardingTour, { useOnboarding } from '../../components/OnboardingTour';
import { RecruiterManual } from '../../components/UserManual';
import { 
  Briefcase, 
  Users, 
  FileText, 
  MessageSquare,
  Plus,
  TrendingUp,
  Loader2,
  AlertCircle,
  Heart,
  Award,
  BarChart3,
  Search,
  Crown,
  Zap,
  ArrowUp
} from 'lucide-react';
import { toast } from 'sonner';

export default function CompanyDashboard() {
  const navigate = useNavigate();
  const { selectedCompany } = useAuth();
  const [company, setCompany] = useState(null);
  const [stats, setStats] = useState({ 
    jobs: 0, 
    solicitations: 0, 
    applications: 0,
    approvedRate: 0,
    inclusionCount: 0
  });
  const [loading, setLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [showOnboarding, setShowOnboarding] = useOnboarding('COMPANY');

  // Recarrega dados quando a empresa selecionada mudar
  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [selectedCompany?.company_id]);

  const handleUpgradePlan = async (plano) => {
    setUpgrading(true);
    try {
      await companiesAPI.upgradePlan(plano);
      const companyRes = await companiesAPI.getMe();
      setCompany(companyRes.data);
      toast.success(`Plano atualizado para ${plano.toUpperCase()}!`);
    } catch (error) {
      toast.error('Erro ao atualizar plano');
    } finally {
      setUpgrading(false);
    }
  };

  const fetchData = async () => {
    try {
      // Passa o company_id explicitamente para garantir que a requisição vai com o header correto
      const companyId = selectedCompany?.company_id;
      const companyRes = await companiesAPI.getMe(companyId);
      setCompany(companyRes.data);
      
      const [jobsRes, solicitationsRes] = await Promise.all([
        jobsAPI.getMy(),
        solicitationsAPI.getSent()
      ]);
      
      const approved = solicitationsRes.data.filter(s => s.status === 'APPROVED').length;
      const total = solicitationsRes.data.length;
      const totalApps = jobsRes.data.reduce((acc, job) => acc + (job.applications_count || 0), 0);
      
      setStats({
        jobs: jobsRes.data.length,
        solicitations: total,
        applications: totalApps,
        approvedRate: total > 0 ? Math.round((approved / total) * 100) : 0,
        inclusionCount: Math.floor(approved * 0.3) // Estimativa
      });
    } catch (error) {
      if (error.response?.status === 404) {
        setNeedsSetup(true);
      } else {
        toast.error('Erro ao carregar dados');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (needsSetup) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="max-w-2xl mx-auto text-center py-12">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">
            Complete seu Cadastro
          </h2>
          <p className="text-slate-600 mb-6">
            Para acessar todas as funcionalidades, você precisa completar o cadastro da empresa.
          </p>
          <Button 
            onClick={() => navigate('/company/setup')}
            className="bg-cyan-600 hover:bg-cyan-700"
          >
            Completar Cadastro
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      {/* Onboarding Tour para primeiro acesso */}
      {showOnboarding && (
        <OnboardingTour 
          userType="COMPANY" 
          onComplete={() => setShowOnboarding(false)} 
        />
      )}
      <div className="space-y-8" data-testid="company-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              Olá, {company?.nome}
            </h1>
            <p className="text-slate-600">
              Gerencie suas vagas e encontre os melhores talentos
            </p>
          </div>
          <div className="flex items-center gap-3">
            <RecruiterManual />
            <Button 
              onClick={() => navigate('/company/jobs/new')}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="create-job-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Vaga
            </Button>
          </div>
        </div>

        {/* Plan Card */}
        {company?.plano !== 'enterprise' && (
          <Card className={`border-2 ${company?.plano === 'plus' ? 'border-purple-500 bg-purple-50' : 'border-slate-200'}`}>
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                    company?.plano === 'plus' ? 'bg-purple-100' : 'bg-slate-100'
                  }`}>
                    {company?.plano === 'plus' ? (
                      <Crown className="w-7 h-7 text-purple-600" />
                    ) : (
                      <Zap className="w-7 h-7 text-slate-600" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900">
                        Plano {company?.plano_info?.nome || 'Básico'}
                      </h3>
                      {company?.plano === 'plus' && (
                        <Badge className="bg-purple-600">Ativo</Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-600">
                      {company?.plano_info?.busca_global 
                        ? '✅ Acesso total à base de candidatos'
                        : '⚠️ Acesso apenas a candidatos que aplicaram às suas vagas'}
                    </p>
                  </div>
                </div>
                
                {company?.plano === 'basic' && (
                  <div className="flex gap-3">
                    <Button
                      onClick={() => handleUpgradePlan('plus')}
                      disabled={upgrading}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {upgrading ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <ArrowUp className="w-4 h-4 mr-2" />
                      )}
                      Upgrade para Plus
                    </Button>
                  </div>
                )}
              </div>
              
              {company?.plano === 'basic' && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <p className="text-sm font-medium text-slate-700 mb-2">Benefícios do Plano Plus:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <Search className="w-4 h-4 text-purple-500" />
                      Busca global de candidatos
                    </div>
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-purple-500" />
                      Até 20 vagas simultâneas
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-purple-500" />
                      50 contatos por mês
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-600">Vagas Ativas</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.jobs}</p>
                </div>
                <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-cyan-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-600">Candidaturas</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.applications}</p>
                </div>
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-600">Taxa de Aceite</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.approvedRate}%</p>
                </div>
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-600">Perfis Inclusão</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.inclusionCount}</p>
                </div>
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Heart className="w-5 h-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`border-2 ${company?.selo_inclusivo ? 'border-cyan-500 bg-cyan-50' : 'border-slate-200'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-600">Selo Inclusivo</p>
                  <p className="text-lg font-bold text-slate-900">
                    {company?.selo_inclusivo ? 'Ativo' : 'Pendente'}
                  </p>
                </div>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  company?.selo_inclusivo ? 'bg-cyan-100' : 'bg-slate-100'
                }`}>
                  <Award className={`w-5 h-5 ${
                    company?.selo_inclusivo ? 'text-cyan-600' : 'text-slate-400'
                  }`} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="border-slate-200 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate('/company/jobs/new')}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-cyan-100 rounded-xl flex items-center justify-center">
                  <Plus className="w-7 h-7 text-cyan-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Publicar Vaga</h3>
                  <p className="text-sm text-slate-600">
                    Crie uma nova oportunidade
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate('/company/search')}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Search className="w-7 h-7 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Buscar Candidatos</h3>
                  <p className="text-sm text-slate-600">
                    Encontre talentos
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate('/company/esg')}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-7 h-7 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Relatório ESG</h3>
                  <p className="text-sm text-slate-600">
                    Métricas de impacto
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Info about launch access */}
        {!company?.acesso_total_lancamento && (
          <Card className="border-cyan-200 bg-cyan-50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Briefcase className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-cyan-900 mb-1">
                    Publique uma vaga e ganhe acesso total!
                  </h3>
                  <p className="text-sm text-cyan-700">
                    Ao publicar uma vaga, você terá acesso completo à base de candidatos 
                    sem precisar solicitar aprovação individual.
                  </p>
                  <Button 
                    className="mt-4 bg-cyan-600 hover:bg-cyan-700"
                    onClick={() => navigate('/company/jobs/new')}
                  >
                    Publicar Vaga
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
